// Queue editor — 6 tabs (General | Users | Notifications | Options |
// Stratus Talk | Advanced) matching 3CX's Add Queue shape. Same skeleton as
// RingGroupEditor; queue-specific fields:
//   • Polling Strategy (instead of Ring Strategy)
//   • Maximum queue wait time
//   • SLA / callback notifications
//   • Music on Hold + Intro Prompt
//   • Announce queue position toggle
//   • Advanced tab for SLA thresholds, wrap-up, agent options

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, Plus, Trash2, Lock, Upload, Mic, FolderOpen, X,
} from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import AudioPicker from '../components/AudioPicker';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const POLLING_STRATEGIES = [
  { v: 'ring_all',         label: 'Ring All' },
  { v: 'longest_idle',     label: 'Longest Idle Agent' },
  { v: 'round_robin',      label: 'Round Robin' },
  { v: 'least_talk',       label: 'Least Talk Time' },
  { v: 'fewest_calls',     label: 'Fewest Calls' },
  { v: 'random',           label: 'Random' },
  { v: 'sequentially_by_agent_order', label: 'Sequential (by agent order)' },
  { v: 'top_down',         label: 'Top Down' },
];

const DESTINATIONS = [
  { v: 'end_call',      label: 'End Call' },
  { v: 'voicemail',     label: 'Voicemail' },
  { v: 'extension',     label: 'Extension' },
  { v: 'ring_group',    label: 'Ring Group' },
  { v: 'queue',         label: 'Queue' },
  { v: 'ivr',           label: 'IVR' },
  { v: 'external',      label: 'External Number' },
  { v: 'accept_anyway', label: 'Accept Anyway (ignore office hours)' },
];

type Tab = 'general' | 'users' | 'notifications' | 'options' | 'talk' | 'advanced';

export default function QueueEditor() {
  const { queue_uuid: routeId } = useParams<{ queue_uuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('general');

  const isNew = !routeId && /\/queues\/new\/?$/.test(location.pathname);
  const queue_uuid = routeId || (isNew ? 'new' : '');

  const queue = useQuery<any>({
    queryKey: ['queue-settings', queue_uuid],
    queryFn: () => fJson(`/api/queues/${queue_uuid}/settings`),
    enabled: !!queue_uuid && !isNew,
  });

  const lockedTab = (t: Tab) => isNew && t !== 'general';
  function pickTab(t: Tab) {
    if (lockedTab(t)) {
      toast('error', 'Save the queue first — the other tabs need an extension to attach to.');
      return;
    }
    setTab(t);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/ring-groups')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Call Handling</button>
        <h1 className="text-xl font-semibold flex-1">
          {isNew ? 'Add Queue' : `Edit Queue${queue.data ? ` — ${queue.data.queue_extension || ''} (${queue.data.queue_name || ''})` : ''}`}
        </h1>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {(['general', 'users', 'notifications', 'options', 'talk', 'advanced'] as Tab[]).map(v => {
          const labels: Record<Tab, string> = {
            general: 'General', users: 'Users', notifications: 'Notifications',
            options: 'Options', talk: 'Stratus Talk', advanced: 'Advanced',
          };
          const locked = lockedTab(v);
          return (
            <button
              key={v}
              onClick={() => pickTab(v)}
              title={locked ? 'Save the queue first to unlock this tab' : undefined}
              className={`px-4 py-2 text-sm whitespace-nowrap inline-flex items-center gap-1 ${
                tab === v ? 'border-b-2 border-primary text-primary'
                : locked ? 'text-textSecondary/40 cursor-not-allowed'
                : 'text-textSecondary hover:text-textPrimary'
              }`}
            >
              {labels[v]}
              {locked && <Lock size={10} />}
            </button>
          );
        })}
      </div>

      {tab === 'general' && (isNew
        ? <NewQueueGeneralPanel toast={toast} navigate={navigate} qc={qc} />
        : (queue.data && <GeneralTab queue={queue.data} queue_uuid={queue_uuid} qc={qc} toast={toast} />))}
      {!isNew && tab === 'users'         && <UsersTab queue_uuid={queue_uuid} toast={toast} qc={qc} />}
      {!isNew && tab === 'notifications' && <NotificationsTab queue_uuid={queue_uuid} toast={toast} qc={qc} />}
      {!isNew && tab === 'options'       && <OptionsTab queue_uuid={queue_uuid} toast={toast} qc={qc} />}
      {!isNew && tab === 'talk'          && <TalkTab queue_uuid={queue_uuid} toast={toast} qc={qc} />}
      {!isNew && tab === 'advanced'      && <AdvancedTab queue_uuid={queue_uuid} toast={toast} qc={qc} />}
    </div>
  );
}

// ── General (create-mode) ──────────────────────────────────────────────────
function NewQueueGeneralPanel({ toast, navigate, qc }: any) {
  const me = api.me();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const [form, setForm] = useState<any>({
    queue_extension: '',
    queue_name: '',
    polling_strategy: 'ring_all',
    domain_uuid: me?.domain_uuid || '',
    ring_time_seconds: 30,
    max_wait_seconds: 1800,
    assigned_dids: '',
    destination_if_no_answer: { type: 'end_call' },
    office_closed_route: { type: 'accept_anyway' },
    on_break_route: { type: 'accept_anyway' },
    on_holiday_route: { type: 'accept_anyway' },
    play_announcement_office_closed: false,
    play_announcement_break: false,
    play_announcement_holiday: false,
  });
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const create = useMutation({
    mutationFn: () => fJson(`/api/tenants/${form.domain_uuid}/queues`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['queues', form.domain_uuid] });
      toast('success', `Queue ${form.queue_extension} created`);
      navigate(`/queues/${r.queue_uuid}/edit`);
    },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Virtual Extension Number *">
          <input className="input" value={form.queue_extension} onChange={e => set('queue_extension', e.target.value)} placeholder="8130" />
        </Field>
        <Field label="Assigned DID number(s)">
          <input className="input" value={form.assigned_dids} onChange={e => set('assigned_dids', e.target.value)} placeholder="comma-separated" />
        </Field>
        <Field label="Queue Name *">
          <input className="input" value={form.queue_name} onChange={e => set('queue_name', e.target.value)} />
        </Field>
        <Field label="Polling Strategy">
          <select className="input" value={form.polling_strategy} onChange={e => set('polling_strategy', e.target.value)}>
            {POLLING_STRATEGIES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Department *">
          <select className="input" value={form.domain_uuid} onChange={e => set('domain_uuid', e.target.value)}>
            <option value="">— Choose —</option>
            {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
          </select>
        </Field>
        <Field label="Ring Time (seconds) *">
          <input className="input" type="number" min={5} max={300} value={form.ring_time_seconds}
                 onChange={e => set('ring_time_seconds', parseInt(e.target.value, 10) || 30)} />
        </Field>
        <Field label="Maximum queue wait time (seconds) *">
          <input className="input" type="number" min={30} max={86400} value={form.max_wait_seconds}
                 onChange={e => set('max_wait_seconds', parseInt(e.target.value, 10) || 1800)} />
        </Field>
      </div>

      <h2 className="font-semibold pt-2">Destinations</h2>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Destination if no answer">
          <select className="input" value={form.destination_if_no_answer?.type} onChange={e => set('destination_if_no_answer', { type: e.target.value })}>
            {DESTINATIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
          </select>
        </Field>
        <DestWithToggle label="When office is closed route to" route={form.office_closed_route}
          announce={form.play_announcement_office_closed}
          onRoute={v => set('office_closed_route', v)} onAnnounce={v => set('play_announcement_office_closed', v)} />
        <DestWithToggle label="When on break route to" route={form.on_break_route}
          announce={form.play_announcement_break}
          onRoute={v => set('on_break_route', v)} onAnnounce={v => set('play_announcement_break', v)} />
        <DestWithToggle label="When on holiday route to" route={form.on_holiday_route}
          announce={form.play_announcement_holiday}
          onRoute={v => set('on_holiday_route', v)} onAnnounce={v => set('play_announcement_holiday', v)} />
      </div>

      <div className="flex justify-end pt-2 border-t border-border">
        <button
          onClick={() => create.mutate()}
          disabled={!form.queue_extension || !form.queue_name || !form.domain_uuid || create.isPending}
          className="btn-primary inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Save size={14} /> {create.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── General (edit-mode) ───────────────────────────────────────────────────
function GeneralTab({ queue, queue_uuid, qc, toast }: any) {
  const [form, setForm] = useState<any>(queue);
  useEffect(() => setForm(queue), [queue.queue_uuid]);
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const save = useMutation({
    mutationFn: () => fJson(`/api/queues/${queue_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-settings', queue_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Virtual Extension Number *">
          <input className="input" value={form.queue_extension || ''} onChange={e => set('queue_extension', e.target.value)} />
        </Field>
        <Field label="Assigned DID number(s)">
          <input className="input" value={form.assigned_dids || ''} onChange={e => set('assigned_dids', e.target.value)} />
        </Field>
        <Field label="Queue Name *">
          <input className="input" value={form.queue_name || ''} onChange={e => set('queue_name', e.target.value)} />
        </Field>
        <Field label="Polling Strategy">
          <select className="input" value={form.polling_strategy || 'ring_all'} onChange={e => set('polling_strategy', e.target.value)}>
            {POLLING_STRATEGIES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Department *">
          <select className="input" value={form.domain_uuid || ''} onChange={e => set('domain_uuid', e.target.value)}>
            <option value="">— Choose —</option>
            {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
          </select>
        </Field>
        <Field label="Ring Time (seconds) *">
          <input className="input" type="number" min={5} max={300} value={form.ring_time_seconds || 30}
                 onChange={e => set('ring_time_seconds', parseInt(e.target.value, 10) || 30)} />
        </Field>
        <Field label="Maximum queue wait time (seconds) *">
          <input className="input" type="number" min={30} max={86400} value={form.max_wait_seconds || 1800}
                 onChange={e => set('max_wait_seconds', parseInt(e.target.value, 10) || 1800)} />
        </Field>
      </div>

      <h2 className="font-semibold pt-2">Destinations</h2>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Destination if no answer">
          <select className="input" value={form.destination_if_no_answer?.type || 'end_call'}
                  onChange={e => set('destination_if_no_answer', { type: e.target.value })}>
            {DESTINATIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
          </select>
        </Field>
        <DestWithToggle label="When office is closed route to" route={form.office_closed_route}
          announce={form.play_announcement_office_closed}
          onRoute={v => set('office_closed_route', v)} onAnnounce={v => set('play_announcement_office_closed', v)} />
        <DestWithToggle label="When on break route to" route={form.on_break_route}
          announce={form.play_announcement_break}
          onRoute={v => set('on_break_route', v)} onAnnounce={v => set('play_announcement_break', v)} />
        <DestWithToggle label="When on holiday route to" route={form.on_holiday_route}
          announce={form.play_announcement_holiday}
          onRoute={v => set('on_holiday_route', v)} onAnnounce={v => set('play_announcement_holiday', v)} />
      </div>

      <div className="flex justify-end pt-2 border-t border-border">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-1">
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Users tab — list of agents in this queue ──────────────────────────────
function UsersTab({ queue_uuid, toast, qc }: any) {
  const list = useQuery<any[]>({
    queryKey: ['queue-users', queue_uuid],
    queryFn: () => fJson(`/api/queues/${queue_uuid}/users`).catch(() => []),
  });
  const exts = useQuery<any[]>({ queryKey: ['users'], queryFn: () => fJson('/api/users') });

  const add = useMutation({
    mutationFn: (extension_uuid: string) => fJson(`/api/queues/${queue_uuid}/users`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ extension_uuid }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-users', queue_uuid] }); toast('success', 'Added'); },
  });
  const remove = useMutation({
    mutationFn: (extension_uuid: string) => fJson(`/api/queues/${queue_uuid}/users/${extension_uuid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queue-users', queue_uuid] }),
  });

  const usedSet = new Set((list.data || []).map((u: any) => u.extension_uuid));
  const [picker, setPicker] = useState('');

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <select className="input flex-1" value={picker} onChange={e => setPicker(e.target.value)}>
          <option value="">Search or enter number…</option>
          {(exts.data || []).filter((u: any) => !usedSet.has(u.extension_uuid))
            .map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name || ''}</option>)}
        </select>
        <button className="btn-primary inline-flex items-center gap-1" disabled={!picker}
                onClick={() => { add.mutate(picker); setPicker(''); }}>
          <Plus size={14} /> Add User
        </button>
      </div>
      <table className="table">
        <thead><tr><th>User</th><th className="w-12"></th></tr></thead>
        <tbody>
          {(list.data || []).map((u: any) => (
            <tr key={u.extension_uuid} className="hover:bg-elevated">
              <td>{u.extension} — {u.name || ''}</td>
              <td>
                <button className="btn-ghost p-1 text-danger" onClick={() => remove.mutate(u.extension_uuid)}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {(list.data || []).length === 0 && (
            <tr><td colSpan={2} className="text-center py-6 text-textSecondary text-sm">No agents yet — add one above.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Notifications tab — supervisors + per-event toggles + voicemail ───────
function NotificationsTab({ queue_uuid, toast, qc }: any) {
  const cfg = useQuery<any>({
    queryKey: ['queue-notifications', queue_uuid],
    queryFn: () => fJson(`/api/queues/${queue_uuid}/notifications`).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  useEffect(() => { if (cfg.data) setForm(cfg.data); }, [cfg.data]);
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/queues/${queue_uuid}/notifications`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-notifications', queue_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-4 space-y-5">
      <Section title="Supervisors">
        <p className="text-sm text-textSecondary">Add users who will be responsible for managing this queue and receiving notifications about its activity.</p>
        <button className="btn inline-flex items-center gap-1 mt-2"><Plus size={14} /> Add</button>
      </Section>

      <Section title="Queue Email Notifications">
        <Toggle label="Notify when SLA time has been breached" v={!!f.notify_sla} on={v => set('notify_sla', v)} />
        <Toggle label="Notify when a callback is made"        v={!!f.notify_callback_made} on={v => set('notify_callback_made', v)} />
        <Toggle label="Notify when a callback fails"          v={!!f.notify_callback_failed} on={v => set('notify_callback_failed', v)} />
        <Toggle label="Notify when a queue call is lost"      v={!!f.notify_call_lost} on={v => set('notify_call_lost', v)} />
      </Section>

      <Section title="Voicemail Greeting">
        <p className="text-sm">When calls go to voicemail, use this custom Voicemail greeting:</p>
        <AudioPickerRow value={f.voicemail_greeting_id || ''} onChange={v => set('voicemail_greeting_id', v)} />
        <p className="text-xs text-textSecondary">Format WAV (PCM, 8 kHz, 16 bit, Mono, max size: 10MB)</p>
      </Section>

      <Section title="Voicemail Delivery Email Options">
        <select className="input max-w-md" value={f.voicemail_email_options || 'attach_delete'} onChange={e => set('voicemail_email_options', e.target.value)}>
          <option value="notify_only">Send email notification only</option>
          <option value="attach_keep">Send voicemail as attachment</option>
          <option value="attach_delete">Send voicemail as attachment and delete from voicemail box</option>
        </select>
      </Section>

      <Section title="Additional Email Recipients">
        <p className="text-sm">Deliver emails to these recipients also</p>
        <input className="input" value={f.additional_email_recipients || ''} onChange={e => set('additional_email_recipients', e.target.value)} placeholder="" />
        <p className="text-xs text-textSecondary">Example: support@mycompany.com, noc@mycompany.com</p>
        <p className="text-xs text-textSecondary mt-2">ⓘ Applies to both queue notifications and voicemail email delivery.</p>
      </Section>

      <div className="flex justify-end">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-1">
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Options tab — language, MoH, Intro Prompt, queue position, transcription ──
function OptionsTab({ queue_uuid, toast, qc }: any) {
  const cfg = useQuery<any>({
    queryKey: ['queue-options', queue_uuid],
    queryFn: () => fJson(`/api/queues/${queue_uuid}/options`).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  useEffect(() => { if (cfg.data) setForm(cfg.data); }, [cfg.data]);
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/queues/${queue_uuid}/options`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-options', queue_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-4 space-y-5">
      <Section title="Queue Language">
        <select className="input max-w-md" value={f.language || 'en-US'} onChange={e => set('language', e.target.value)}>
          <option value="en-US">Standard English Prompts Set</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
        </select>
      </Section>

      <Section title="Music on Hold">
        <AudioPickerRow value={f.moh_audio_id || ''} onChange={v => set('moh_audio_id', v)} placeholder="onhold.wav" />
        <p className="text-xs text-textSecondary">Format WAV (PCM, 8 kHz, 16 bit, Mono, max size: 10MB)</p>
      </Section>

      <Section title="Intro Prompt">
        <Toggle label="Intro Prompt" v={!!f.intro_prompt_enabled} on={v => set('intro_prompt_enabled', v)} />
        <AudioPickerRow value={f.intro_prompt_audio_id || ''} onChange={v => set('intro_prompt_audio_id', v)} disabled={!f.intro_prompt_enabled} />
        <p className="text-xs text-textSecondary">Format WAV (PCM, 8 kHz, 16 bit, Mono, max size: 10MB)</p>
        <Toggle label="Play full intro prompt before calling Agents" v={!!f.intro_prompt_full} on={v => set('intro_prompt_full', v)} />
      </Section>

      <Section title="">
        <Toggle label="Announce queue position to the caller" v={!!f.announce_position} on={v => set('announce_position', v)} />
        <Field label="Announcement Interval (seconds) *">
          <input className="input max-w-[120px]" type="number" min={10} max={600} value={f.announcement_interval || 60}
                 onChange={e => set('announcement_interval', parseInt(e.target.value, 10) || 60)}
                 disabled={!f.announce_position} />
        </Field>
      </Section>

      <Section title="Transcription">
        <Field label="Select what to transcribe">
          <select className="input max-w-md" value={f.transcription_mode || 'department'} onChange={e => set('transcription_mode', e.target.value)}>
            <option value="department">Use department settings</option>
            <option value="never">Don't transcribe</option>
            <option value="voicemail_only">Voicemails only</option>
            <option value="always">Always</option>
          </select>
        </Field>
      </Section>

      <div className="flex justify-end">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-1">
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Stratus Talk tab ─────────────────────────────────────────────────────
function TalkTab({ queue_uuid, toast, qc }: any) {
  const cfg = useQuery<any>({
    queryKey: ['queue-talk', queue_uuid],
    queryFn: () => fJson(`/api/talk/queue-config/${queue_uuid}`).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  const host = window.location.host;

  const save = useMutation({
    mutationFn: () => fJson(`/api/talk/queue-config/${queue_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-talk', queue_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-6 space-y-4 max-w-2xl">
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
        Enable Stratus Talk and Meet to connect with a single click in your email signature and website
      </label>
      <Field label="Your Link Name: *">
        <input className="input" value={f.link_name || ''} onChange={e => set('link_name', e.target.value)} />
      </Field>
      <Field label="Talk URL">
        <a href={`https://${host}/${f.link_name || ''}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
          https://{host}/{f.link_name || '<slug>'}
        </a>
      </Field>
      <Field label="Ask customer for">
        <select className="input" value={f.ask_for || 'name_email'} onChange={e => set('ask_for', e.target.value)}>
          <option value="name_email">Name & Email Address</option>
          <option value="name">Name only</option>
          <option value="nothing">Nothing</option>
        </select>
      </Field>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

// ── Advanced tab — matches 3CX screenshot:
//   Allow call back (DTMF 2 / timeout) + Callback Outbound Prefix
//   Queue Preferences (Wrap-up / Max callers / SLA time / Priority / Target busy agents)
//   Record Calls (DTMF 3 trigger, opt-out or opt-in)
//   Chat Assignment (Take Control vs Auto Assign)
//   Schedule Queue Statistics Reset (timer + manual Reset button)
function AdvancedTab({ queue_uuid, toast, qc }: any) {
  const cfg = useQuery<any>({
    queryKey: ['queue-advanced', queue_uuid],
    queryFn: () => fJson(`/api/queues/${queue_uuid}/advanced`).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  useEffect(() => { if (cfg.data) setForm(cfg.data); }, [cfg.data]);
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/queues/${queue_uuid}/advanced`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-advanced', queue_uuid] }); toast('success', 'Saved'); },
  });

  const resetStats = useMutation({
    mutationFn: () => fJson(`/api/queues/${queue_uuid}/reset-statistics`, { method: 'POST' }),
    onSuccess: () => toast('success', 'Statistics reset'),
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-5">
      {/* Allow call back */}
      <div className="space-y-2">
        <Toggle label="Allow call back" v={!!f.allow_callback} on={v => set('allow_callback', v)} bold />
        <p className="text-xs text-textSecondary">
          Customers in queue can either trigger call back by pressing DTMF 2 or choose the callback option after call back timeout.
        </p>
        <div className={`space-y-2 pl-2 ${!f.allow_callback ? 'opacity-50' : ''}`}>
          <label className="text-sm flex items-center gap-2">
            <input type="radio" disabled={!f.allow_callback}
                   checked={f.callback_trigger === 'dtmf2'}
                   onChange={() => set('callback_trigger', 'dtmf2')} />
            Triggered on user request (Press 2)
          </label>
          <div className="flex items-center gap-2">
            <label className="text-sm flex items-center gap-2">
              <input type="radio" disabled={!f.allow_callback}
                     checked={f.callback_trigger === 'timeout'}
                     onChange={() => set('callback_trigger', 'timeout')} />
              Call back offered after
            </label>
            <input className="input max-w-[120px]" type="number" min={0} max={3600}
                   disabled={!f.allow_callback || f.callback_trigger !== 'timeout'}
                   value={f.callback_offer_after_seconds || ''}
                   onChange={e => set('callback_offer_after_seconds', parseInt(e.target.value, 10) || 0)} />
            <span className="text-sm text-textSecondary">seconds</span>
          </div>
          <Field label="Callback Outbound Prefix">
            <input className="input max-w-md" disabled={!f.allow_callback}
                   value={f.callback_outbound_prefix || ''}
                   onChange={e => set('callback_outbound_prefix', e.target.value)} />
          </Field>
        </div>
      </div>

      {/* Queue Preferences */}
      <Section title="Queue Preferences">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Wrap-up Time (seconds) *">
            <input className="input" type="number" min={0} max={600} value={f.wrap_up_seconds ?? 2}
                   onChange={e => set('wrap_up_seconds', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="Maximum callers in queue *">
            <input className="input" type="number" min={0} max={1000} value={f.max_callers ?? 0}
                   onChange={e => set('max_callers', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="Configure SLA Time (seconds) *">
            <input className="input" type="number" min={0} max={3600} value={f.sla_time_seconds ?? 0}
                   onChange={e => set('sla_time_seconds', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <div className="pt-6">
            <Toggle label="Priority Queue" v={!!f.priority_queue} on={v => set('priority_queue', v)} />
          </div>
        </div>
        <Toggle
          label='Target agents already on a call. Requires "Accept multiple calls" option.'
          v={!!f.target_busy_agents} on={v => set('target_busy_agents', v)}
        />
      </Section>

      {/* Record Calls */}
      <div className="space-y-2 border-t border-border pt-4">
        <Toggle label="Record Calls" v={!!f.record_calls} on={v => set('record_calls', v)} bold />
        <p className="text-xs text-textSecondary">
          Configure recording settings for calls to this queue when users press DTMF 3
        </p>
        <div className={`space-y-1 pl-2 ${!f.record_calls ? 'opacity-50' : ''}`}>
          <label className="text-sm flex items-center gap-2">
            <input type="radio" disabled={!f.record_calls}
                   checked={f.record_consent_mode === 'opt_out'}
                   onChange={() => set('record_consent_mode', 'opt_out')} />
            Allow caller to opt-out of being recorded
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="radio" disabled={!f.record_calls}
                   checked={f.record_consent_mode === 'opt_in'}
                   onChange={() => set('record_consent_mode', 'opt_in')} />
            Ask caller to be recorded (opt-in)
          </label>
        </div>
      </div>

      {/* Chat Assignment */}
      <Section title="Chat Assignment">
        <p className="text-sm font-medium">Chat Ownership Assignment</p>
        <label className="text-sm flex items-center gap-2">
          <input type="radio" checked={f.chat_assignment === 'take_control'} onChange={() => set('chat_assignment', 'take_control')} />
          Take Control: Agents "take" to manage a conversation
        </label>
        <label className="text-sm flex items-center gap-2">
          <input type="radio" checked={(f.chat_assignment ?? 'auto_assign') === 'auto_assign'} onChange={() => set('chat_assignment', 'auto_assign')} />
          Auto Assign: First responding Agent assigned
        </label>
      </Section>

      {/* Schedule Queue Statistics Reset */}
      <div className="space-y-2 border-t border-border pt-4">
        <Toggle label="Schedule Queue Statistics Reset" v={!!f.schedule_stats_reset} on={v => set('schedule_stats_reset', v)} bold />
        <div className="flex items-center gap-2">
          <span className="text-sm">Reset Call Statistics for this Queue</span>
          <button onClick={() => resetStats.mutate()} className="btn-primary inline-flex items-center gap-1 text-sm">
            <Save size={12} /> Reset
          </button>
        </div>
        <div className={`space-y-2 ${!f.schedule_stats_reset ? 'opacity-50' : ''}`}>
          <p className="text-sm">Select when you want the Queue Statistics to be reset</p>
          <div className="flex items-center gap-2">
            <select className="input max-w-[140px]" disabled={!f.schedule_stats_reset}
                    value={f.stats_reset_frequency || 'daily'}
                    onChange={e => set('stats_reset_frequency', e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <input type="number" min={1} max={12} className="input max-w-[80px]" disabled={!f.schedule_stats_reset}
                   value={f.stats_reset_hour || 12}
                   onChange={e => set('stats_reset_hour', parseInt(e.target.value, 10) || 12)} />
            <span>:</span>
            <input type="number" min={0} max={59} className="input max-w-[80px]" disabled={!f.schedule_stats_reset}
                   value={String(f.stats_reset_minute ?? 0).padStart(2, '0')}
                   onChange={e => set('stats_reset_minute', parseInt(e.target.value, 10) || 0)} />
            <select className="input max-w-[80px]" disabled={!f.schedule_stats_reset}
                    value={f.stats_reset_ampm || 'AM'}
                    onChange={e => set('stats_reset_ampm', e.target.value)}>
              <option>AM</option>
              <option>PM</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t border-border">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-1">
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function DestWithToggle({ label, route, announce, onRoute, onAnnounce }: any) {
  return (
    <div>
      <Field label={label}>
        <select className="input" value={route?.type || 'accept_anyway'} onChange={e => onRoute({ type: e.target.value })}>
          {DESTINATIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
        </select>
      </Field>
      <label className="text-sm flex items-center gap-2 mt-1">
        <input type="checkbox" checked={!!announce} onChange={e => onAnnounce(e.target.checked)} />
        Play Announcement
      </label>
    </div>
  );
}

function AudioPickerRow({ value, onChange, placeholder, disabled }: { value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean }) {
  return <AudioPicker value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} />;
}

function Section({ title, children }: any) {
  return (
    <div className="space-y-2">
      {title && <div className="text-sm font-semibold border-b border-border pb-1">{title}</div>}
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, v, on, bold }: { label: string; v: boolean; on: (v: boolean) => void; bold?: boolean }) {
  return (
    <label className={`text-sm flex items-center gap-2 py-1 ${bold ? 'font-semibold' : ''}`}>
      <input type="checkbox" checked={v} onChange={e => on(e.target.checked)} />
      {label}
    </label>
  );
}
